import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, PresaleRow } from '../../api';
import { presaleEndpoints } from '../../api';
import { Badge, Button, EmptyRow, Field, Panel, Table } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, utcToZoned, zonedToUtc } from './shared';
import { useFeatures } from '../../features';
import { dateWords } from '../../terms';

/**
 * Event → Presales (D-141).
 *
 * A presale is a door that opens before the public one. The window it opens is
 * the event's WEB channel window: set the performance ON_SALE, leave the public
 * window closed or in the future, and a presale lets code holders through
 * early. That is said on the page, because it is the one thing an operator
 * has to get right and nothing else would tell them.
 *
 * The customer's credential is a promo code of type "Access only" pointing at
 * the presale — not `presales.code`, which is the operator's own label. The
 * codes are made on the Promo codes page; this panel says so rather than
 * offering a field that would look like a code and not be one.
 */

const ACCESS_TYPES = [
  { code: 'ACCESS_CODE', label: 'Access code', built: true },
  { code: 'LOGGED_IN', label: 'Any signed-in customer', built: false },
  { code: 'CUSTOMER_LIST', label: 'Named customer list', built: false },
  // Listed only where loyalty exists at all (D-369).
  { code: 'LOYALTY_VERIFIED', label: 'Verified by loyalty rewards', built: false, loyalty: true },
];

const blank = { performance_id: '', code: '', name: '', starts_at: '', ends_at: '', ticket_limit_per_customer: '' };

export function PresalesPanel({ detail, onError, can }: PanelProps) {
  const features = useFeatures();
  const eventId = detail.event.id;
  const timezone = detail.venue.timezone;
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...blank });

  const presales = useQuery<{ presales: PresaleRow[] }, ApiError>({ queryKey: ['presales', eventId], queryFn: () => presaleEndpoints.list(eventId) });
  const editable = detail.event.status !== 'ARCHIVED' && can('performances.manage');
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['presales', eventId] });

  const create = useMutation({
    mutationFn: () =>
      presaleEndpoints.create(eventId, {
        ...(form.performance_id ? { performance_id: form.performance_id } : {}),
        code: form.code.trim(),
        name: form.name.trim(),
        access_type: 'ACCESS_CODE',
        starts_at: zonedToUtc(form.starts_at, timezone),
        ends_at: zonedToUtc(form.ends_at, timezone),
        ...(form.ticket_limit_per_customer ? { ticket_limit_per_customer: Number(form.ticket_limit_per_customer) } : {}),
      }),
    onSuccess: () => {
      setCreating(false);
      setForm({ ...blank });
      invalidate();
    },
    onError: (cause: ApiError) => onError(cause),
  });

  const setActive = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => presaleEndpoints.update(eventId, input.id, { active: input.active }),
    onSuccess: invalidate,
    onError: (cause: ApiError) => onError(cause),
  });

  const performanceName = (id: string | null) => {
    if (!id) return `All ${dateWords().many}`;
    const performance = detail.performances.find((p) => p.id === id);
    return performance ? `${utcToZoned(performance.starts_at, timezone).replace('T', ' ')}` : `One ${dateWords().one}`;
  };

  const rows = presales.data?.presales ?? [];

  return (
    <div className="space-y-4">
      <Panel
        title="Presales"
        description={`A presale lets code holders buy before the public can. Set the ${dateWords().one} on sale, leave its online window closed or in the future, and a presale opens it early for anyone with an access code. It never opens ${dateWords().a} that is not on sale, and it never reopens a window that has already closed.`}
        actions={editable && !creating ? <Button onClick={() => setCreating(true)}>New presale</Button> : null}
      >
        {creating ? (
          <form
            className="grid gap-3 border-t border-line px-5 py-4 md:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Name" htmlFor="presale-name" hint="What staff call it. Customers never see this.">
              <input id="presale-name" className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} />
            </Field>
            <Field label="Reference" htmlFor="presale-code" hint="A short internal label, unique for this event. This is not the code customers type.">
              <input id="presale-code" className={INPUT} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required maxLength={40} />
            </Field>
            <Field label={`${dateWords().One}`} htmlFor="presale-performance" hint={`Leave as all ${dateWords().many} unless only one night is in the presale.`}>
              <select id="presale-performance" className={INPUT} value={form.performance_id} onChange={(e) => setForm({ ...form, performance_id: e.target.value })}>
                <option value="">All {dateWords().many}</option>
                {detail.performances.map((p) => (
                  <option key={p.id} value={p.id}>
                    {utcToZoned(p.starts_at, timezone).replace('T', ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Access" htmlFor="presale-access" hint="Only access codes are built. The others are refused rather than quietly ignored.">
              <select id="presale-access" className={INPUT} value="ACCESS_CODE" disabled>
                {ACCESS_TYPES.filter((type) => !('loyalty' in type) || features.loyalty).map((type) => (
                  <option key={type.code} value={type.code} disabled={!type.built}>
                    {type.label}
                    {type.built ? '' : ' — not built yet'}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Opens" htmlFor="presale-starts" hint={`Venue time (${timezone}).`}>
              <input id="presale-starts" type="datetime-local" className={INPUT} value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} required />
            </Field>
            <Field label="Closes" htmlFor="presale-ends">
              <input id="presale-ends" type="datetime-local" className={INPUT} value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} required />
            </Field>
            <Field label="Tickets per order" htmlFor="presale-limit" hint="Optional. Enforced per order; a customer can place a second order.">
              <input id="presale-limit" type="number" min={1} className={INPUT} value={form.ticket_limit_per_customer} onChange={(e) => setForm({ ...form, ticket_limit_per_customer: e.target.value })} />
            </Field>
            <div className="flex items-end gap-2">
              <Button type="submit" variant="primary" disabled={create.isPending}>
                {create.isPending ? 'Saving…' : 'Create presale'}
              </Button>
              <Button type="button" onClick={() => { setCreating(false); setForm({ ...blank }); }}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </Panel>

      <Panel title="All presales" description="Codes that unlock these are made on the Promo codes page, as a discount of type “Access only” pointing at the presale.">
        <Table headers={['Name', 'Reference', `${dateWords().One}`, 'Opens', 'Closes', 'Per order', 'Status', '']}>
          {rows.length === 0 ? (
            <EmptyRow columns={8}>No presales on this event.</EmptyRow>
          ) : (
            rows.map((presale) => (
              <tr key={presale.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm font-medium">{presale.name}</td>
                <td className="px-5 py-3 text-sm text-ink-500">{presale.code}</td>
                <td className="px-5 py-3 text-sm">{performanceName(presale.performance_id)}</td>
                <td className="px-5 py-3 text-sm">{utcToZoned(presale.starts_at, timezone).replace('T', ' ')}</td>
                <td className="px-5 py-3 text-sm">{utcToZoned(presale.ends_at, timezone).replace('T', ' ')}</td>
                <td className="px-5 py-3 text-sm tabular">{presale.ticket_limit_per_customer ?? '—'}</td>
                <td className="px-5 py-3 text-sm">
                  <Badge tone={presale.active ? 'ok' : 'neutral'}>{presale.active ? 'Active' : 'Retired'}</Badge>
                </td>
                <td className="px-5 py-3 text-right text-sm">
                  {editable ? (
                    <Button onClick={() => setActive.mutate({ id: presale.id, active: !presale.active })} disabled={setActive.isPending}>
                      {presale.active ? 'Retire' : 'Reinstate'}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
